export const learnFr = {
  meta: {
    title: "Apprendre à apprendre — Lumen",
    description: "Le guide interactif pour comprendre comment fonctionnent la mémoire, le cerveau et les meilleures méthodes d'étude.",
  },
  header: {
    badge: "Guide",
    title: "Apprendre à apprendre",
    subtitlePre: "Tu n'as pas besoin d'étudier plus. Tu dois étudier ",
    subtitleBold: "mieux",
    subtitlePost: ". Voici comment fonctionne vraiment ton esprit.",
  },
  science: { heading: "La science de l'étude" },
  methodsHeading: "Méthodes d'étude",
  finalTip: {
    title: "Le dernier conseil",
    body: "Il n'existe pas de méthode parfaite pour tout le monde. Essaie, mesure, adapte. Ton tuteur IA t'aidera à choisir la bonne méthode pour chaque matière.",
  },
  lvl: { easy: "Facile", media: "Moyen" },
  badge: { accessible: "Accessible" },
  methodsList: { when: "Quand :" },
  dialog: {
    howTo: "Comment faire",
    example: "Exemple pratique",
    adaptationsTitle: "Variantes accessibles",
    adaptationsFor: "Adaptations pour",
    noAdaptations: "Pas de variante spécifique : la méthode de base est déjà adaptée.",
  },
  adaptMeta: {
    dsa: { label: "TSA", short: "dyslexie, dysorthographie, dyscalculie" },
    adhd: { label: "TDAH", short: "attention et hyperactivité" },
    bes: { label: "BEP", short: "besoins éducatifs particuliers" },
    focus: { label: "Concentration", short: "difficulté à rester concentré" },
  },
  chapters: {
    memory: {
      title: "Comment fonctionne la mémoire",
      body: "La mémoire n'est pas un tiroir : c'est un muscle. Chaque fois que tu RAPPELLES une information — au lieu de la relire — tu la fixes plus profondément. On appelle cela le **Rappel Actif**.\n\nLa courbe de l'oubli (Ebbinghaus) montre qu'après 24h on se souvient de 30% de ce qu'on a lu passivement. En révisant de façon espacée (répétition espacée), la rétention grimpe à 80–90%.",
    },
    brain: {
      title: "Comment fonctionne le cerveau quand on étudie",
      body: "Le cerveau a deux modes : **focalisé** et **diffus** (détendu). Les alterner — avec des pauses et du sommeil — est ce qui permet à l'apprentissage de se consolider. Étudier 8h d'affilée sans pause est contre-productif.",
    },
    mistakes: {
      title: "Les erreurs les plus courantes",
      body: "• **Relire** au lieu de répéter à voix haute.\n• Étudier de manière **passive** avec des surlignages colorés sans jamais se rappeler.\n• Tout bachoter au dernier moment.\n• Ne pas dormir assez : sans sommeil paradoxal, la mémoire ne se consolide pas.",
    },
    notes: {
      title: "Comment prendre des notes",
      body: "La méthode **Cornell** : diviser la page en 3 zones (notes, mots-clés, résumé). À la fin du cours, ferme le livre et remplis le résumé de mémoire. Cela augmente la rétention jusqu'à 34%.",
    },
    anxiety: {
      title: "Comment gérer le stress des contrôles",
      body: "La respiration 4-7-8 (inspire 4s, retiens 7s, expire 8s) fait baisser le cortisol. La veille, n'étudie pas de nouveaux sujets : contente-toi de **réviser**. La confiance vient de la préparation, pas de l'anxiété.",
    },
    forgetting: {
      title: "Comment éviter d'oublier",
      body: "Révise un sujet après 1 jour, 3 jours, 7 jours, 21 jours. Chaque rappel le renforce. C'est le secret des étudiants en médecine.",
    },
  },
  methods: {
    feynman: {
      d: "Explique-le comme à un enfant de 10 ans.",
      when: "Quand tu dois vraiment comprendre un concept.",
      how: [
        "Choisis un concept et écris-le en haut d'une page.",
        "Explique-le à voix haute comme si tu parlais à un enfant de 10 ans.",
        "Note où tu bloques ou utilises des mots difficiles.",
        "Retourne au livre uniquement sur ces points, puis explique à nouveau depuis le début.",
      ],
      example: "Concept : la photosynthèse.\n\nExplication pour un enfant : \"Les plantes mangent de la lumière. Elles prennent l'eau par les racines et l'air par les feuilles, et avec la lumière du soleil elles cuisinent du sucre pour grandir. L'air qu'on respire est leur 'déchet'.\"\n\nBlocage typique : tu ne sais pas dire quel est le rapport avec la chlorophylle. → Tu retournes au livre, tu découvres que c'est la molécule verte qui capte la lumière, et tu réexpliques : \"La chlorophylle est comme un panneau solaire dans la feuille.\"",
      tip: "Si tu ne peux pas l'expliquer simplement, tu ne l'as pas encore compris.",
      adaptations: {
        dsa: [
          "Enregistre l'explication à voix haute plutôt que de l'écrire — tu pourras la réécouter.",
          "Utilise le tuteur IA de l'appli pour reformuler le concept avec des mots plus simples.",
          "Utilise une police à haute lisibilité et active le mode TSA dans les paramètres.",
        ],
        adhd: [
          "Un concept à la fois, avec un minuteur visible de 10 minutes.",
          "Explique en marchant ou en gesticulant : le mouvement aide à rester concentré.",
          "Récompense immédiate à la fin (5 min de musique, collation, pause).",
        ],
        bes: [
          "Utilise des dessins ou des icônes au lieu de mots difficiles.",
          "Demande à un ami/parent de t'écouter : le retour réduit l'anxiété.",
          "Découpe l'explication en 3 mini-parties de 2 minutes chacune.",
        ],
        focus: [
          "Élimine les distractions : téléphone dans une autre pièce, notifications coupées.",
          "Explique à voix haute comme si tu tournais une vidéo : cela déclenche plus d'attention.",
        ],
      },
    },
    activeRecall: {
      d: "Ferme le livre. Répète à voix haute.",
      when: "Toujours. C'est la méthode n°1.",
      how: [
        "Lis un paragraphe ou une page.",
        "Ferme le livre.",
        "Répète à voix haute (ou écris) tout ce dont tu te souviens.",
        "Rouvre-le et corrige les erreurs.",
      ],
      example: "Histoire — chapitre sur la Révolution française.\n\n1) Lis les causes (1789).\n2) Ferme le livre et essaie de dire : \"Crise économique, inégalité entre les trois ordres, influence des Lumières, prise de la Bastille le 14 juillet…\"\n3) Rouvre-le : tu as oublié la famine de 1788. Marque-le en rouge.\n4) Réessaie après 10 minutes — cette fois tu t'en souviens.",
      tip: "Relire ≠ étudier. Si tu ne peux pas te le rappeler, tu ne le sais pas.",
      adaptations: {
        dsa: [
          "Répète à voix haute plutôt que d'écrire : cela évite la lecture et l'écriture fatigantes.",
          "Utilise des flashcards audio : enregistre la question et écoute-la avant de répondre.",
          "Demande au tuteur IA de te poser des questions orales sur ce que tu as étudié.",
        ],
        adhd: [
          "Sessions courtes de 5-10 minutes, puis change de sujet pour éviter l'ennui.",
          "Transforme la révision en jeu où tu marques des points contre toi-même.",
          "Debout ou en marchant : le mouvement aide le rappel.",
        ],
        bes: [
          "Commence par 3 questions simples avant d'augmenter la difficulté.",
          "Utilise des images guides pour déclencher le rappel (mémoire visuelle).",
        ],
        focus: [
          "Minuteur court (10 min) et un objectif clair : \"rappelle 5 points\".",
          "Écris la réponse tout de suite sans relire : cela active immédiatement le rappel.",
        ],
      },
    },
    pomodoro: {
      d: "25 min de concentration + 5 min de pause.",
      when: "Pour commencer à étudier sans procrastiner.",
      how: [
        "Choisis UNE tâche précise (pas 'étudier l'histoire', mais 'lire les pages 45–52').",
        "Minuteur de 25 min. Téléphone dans une autre pièce.",
        "5 min de vraie pause (se lever, boire, regarder dehors).",
        "Après 4 pomodoros : une longue pause de 15–30 min.",
      ],
      example: "15h00 — Pomodoro 1 : lire les pages 45–52 d'histoire.\n15h25 — Pause : marcher dans la cuisine, boire de l'eau.\n15h30 — Pomodoro 2 : fermer le livre et faire du Rappel Actif du chapitre.\n15h55 — Pause.\n16h00 — Pomodoro 3 : résoudre 5 exercices de maths.\n16h25 — Longue pause, 20 min.",
      tip: "Utilise le minuteur d'étude de l'appli → mode Pomodoro.",
      adaptations: {
        dsa: [
          "Pendant le pomodoro alterne 10 min de lecture + 15 min d'activité orale/visuelle.",
          "Pendant la pause ne lis pas : repose tes yeux (regarde au loin, lumière naturelle).",
        ],
        adhd: [
          "Réduis à **15 min de concentration + 5 min de pause** — plus réaliste pour le TDAH.",
          "Minuteur visible et sonore. Écris la tâche sur un post-it devant toi.",
          "Si tu te déconcentres, ne te juge pas : remarque la pensée, reviens à la tâche.",
        ],
        bes: [
          "Commence par un seul pomodoro par jour, puis augmente progressivement.",
          "Utilise un minuteur analogique ou un sablier : le temps devient concret.",
        ],
        focus: [
          "Mode avion obligatoire pendant les 25 minutes.",
          "VRAIE pause : pas de réseaux sociaux, pas de vidéo. Juste du mouvement ou de l'eau.",
        ],
      },
    },
    spacedRepetition: {
      d: "Réviser à intervalles croissants.",
      when: "Pour retenir sur le long terme.",
      how: [
        "Étudie aujourd'hui (jour 0).",
        "Révise après 1 jour.",
        "Puis après 3 jours, 7 jours, 21 jours.",
        "Chaque fois que tu te trompes, recommence à 1 jour.",
      ],
      example: "Vocabulaire d'anglais, 20 nouveaux mots le lundi.\n\n• Mar (jour 1) : rappelle les 20 mots. Tu en rates 4 → marque-les.\n• Jeu (jour 3) : rappelle-les tous. Remets les 4 ratés au jour 1.\n• Lundi suivant (jour 7) : révise seulement ceux qui sont stables.\n• Dans 3 semaines : révision finale, 5 minutes.\n\nRésultat : 90% de rétention après un mois, contre 30% avec la simple relecture.",
      tip: "Les flashcards de l'appli utilisent déjà cette méthode (5 boîtes de Leitner).",
      adaptations: {
        dsa: [
          "Préfère les flashcards avec images ou audio, pas seulement du texte.",
          "Dans l'appli tu peux créer des flashcards vocales : enregistre la réponse à voix haute.",
        ],
        adhd: [
          "Maximum 10 cartes par session : les petits paquets évitent d'abandonner.",
          "Révise toujours à la même heure : la routine aide plus que la volonté.",
        ],
        bes: [
          "Utilise des couleurs différentes par matière : cela aide à retrouver les bonnes cartes.",
          "Demande de l'aide pour créer les premières cartes, ensuite tu deviendras autonome.",
        ],
        focus: [
          "10 minutes le matin juste après le réveil : le cerveau est plus réceptif.",
          "Ferme tous les autres onglets/applis avant de commencer le paquet.",
        ],
      },
    },
    cornellNotes: {
      d: "Notes en 3 zones : notes / mots-clés / résumé.",
      when: "Pendant les cours.",
      how: [
        "Divise la page en 3 zones : colonne de gauche (mots-clés), colonne de droite (notes), bande du bas (résumé).",
        "Pendant le cours écris uniquement dans la colonne des notes (à droite).",
        "Juste après, remplis les mots-clés à gauche.",
        "Le soir, écris 2-3 lignes de résumé en bas.",
      ],
      example: "Page de biologie.\n\n[Notes à droite] La mitose a 4 phases : prophase, métaphase, anaphase, télophase…\n[Mots à gauche] mitose · 4 phases · chromosomes\n[Résumé en bas] La mitose divise une cellule en 2 identiques. 4 phases. Utilisée pour la croissance et la réparation.",
      tip: "En couvrant la colonne de droite tu peux utiliser les mots-clés comme flashcards.",
      adaptations: {
        dsa: [
          "Écris en lettres bâtons ou utilise une tablette avec une police à haute lisibilité.",
          "Enregistre le cours (avec permission) et remplis les notes après, calmement.",
          "Dans la colonne de gauche utilise des symboles/icônes au lieu de mots quand tu peux.",
        ],
        adhd: [
          "Concentre-toi uniquement sur la colonne de droite pendant le cours : une chose à la fois.",
          "Remplis mots-clés et résumé JUSTE après le cours, pas le lendemain.",
        ],
        bes: [
          "Modèle pré-imprimé avec les 3 zones déjà divisées : réduit l'effort d'organisation.",
          "Résumé en 2 phrases maximum : mieux vaut court et terminé que long et abandonné à moitié.",
        ],
        focus: [
          "Stylo toujours en main : écrire maintient l'attention même si tu ne copies pas tout.",
        ],
      },
    },
    blurting: {
      d: "Écris tout ce dont tu te souviens, puis compare.",
      when: "Avant un contrôle.",
      how: [
        "Choisis un sujet.",
        "Feuille blanche. 5-10 min : écris TOUT ce dont tu te souviens, dans le désordre.",
        "Ouvre le livre et compare.",
        "Entoure en rouge les lacunes. Ce sera ta prochaine session d'étude.",
      ],
      example: "Sujet : le système circulatoire.\n\nBlurt : \"Cœur, 4 cavités, oreillette droite et gauche, ventricules, les artères transportent le sang oxygéné sauf l'artère pulmonaire, les veines…\"\n\nOuvre le livre : tu as oublié la valve tricuspide et le rôle des capillaires. C'est ce qu'il faut réviser ce soir.",
      tip: "Le blurting rend les lacunes visibles en 10 minutes. Fais-le la veille d'un contrôle.",
      adaptations: {
        dsa: [
          "Fais le blurting à voix haute : enregistre l'audio plutôt que d'écrire.",
          "En comparant avec le livre, utilise des surligneurs colorés pour les \"lacunes\".",
        ],
        adhd: [
          "Minuteur de 5 minutes : quand il sonne, arrête, même si tu n'as pas fini.",
          "Un sujet à la fois. Ne passe pas au suivant avant d'avoir comparé.",
        ],
        bes: [
          "Tu peux utiliser des schémas/flèches au lieu de phrases complètes.",
          "Le premier blurt est toujours plein de lacunes : c'est NORMAL, pas un échec.",
        ],
        focus: [
          "Feuille blanche devant toi, tout le reste loin. Écris d'un seul jet sans t'arrêter.",
        ],
      },
    },
    mindMapping: {
      d: "Représenter les concepts visuellement.",
      when: "Pour relier des idées complexes.",
      how: [
        "Écris le concept central au milieu d'une page.",
        "Ramifie avec 3-5 branches principales (sous-thèmes).",
        "Chaque branche se divise en sous-branches avec détails, exemples, dates.",
        "Utilise des couleurs différentes pour les macro-zones et des flèches pour les connexions.",
      ],
      example: "Centre : \"Seconde Guerre mondiale\".\n\nBranches : Causes · Fronts · Personnages · Conséquences.\n\n• Causes → Traité de Versailles, crise de 1929, montée du nazisme.\n• Fronts → Europe, Pacifique, Afrique.\n• Personnages → Hitler, Churchill, Roosevelt, Staline.\n• Conséquences → ONU, guerre froide, décolonisation.\n\nFlèches entre 'crise de 1929' et 'montée du nazisme' pour montrer la cause-effet.",
      tip: "Dans l'appli tu peux générer des cartes mentales à partir de tes notes dans la section Étude.",
      adaptations: {
        dsa: [
          "Méthode IDÉALE pour les TSA : exploite la mémoire visuelle et réduit le texte.",
          "Utilise des couleurs, icônes et dessins au lieu de longues phrases.",
          "Dans l'appli tu peux générer la carte à partir de tes notes en un clic.",
        ],
        adhd: [
          "Pars du centre sans trop planifier : ajoute des branches au fil de tes pensées.",
          "Si tu te perds, arrête tout et reviens au concept central.",
        ],
        bes: [
          "Petite carte, maximum 3 branches principales. Mieux vaut simple et clair.",
          "Une couleur par branche aide à mémoriser les connexions.",
        ],
        focus: [
          "Construis la carte à la main sur une grande feuille : le geste te maintient éveillé.",
          "Fais une carte à la fois par sujet — ne mélange pas les matières.",
        ],
      },
    },
  },
  toast: {
    mustLogin: "Tu dois être connecté pour enregistrer",
    saved: "Résultat enregistré dans ton profil ✨",
  },
  quiz: {
    cta: {
      discoverTitle: "Découvre ta méthode",
      discoverSubtitle: "7 questions rapides pour découvrir quelle méthode d'étude fonctionne le mieux pour toi.",
      yourMethod: "Ta méthode",
      recommendedBadge: "Recommandée",
      savedOn: "enregistré le {{date}}",
      thenPrefix: " · puis ",
      andWord: " et ",
      viewCard: "Voir la fiche",
      retakeTest: "Refaire le test",
    },
    dialogTitle: "Découvre ta méthode",
    questionOf: "Question {{n}} sur {{total}}",
    resultsSubtitle: "Voici les méthodes qui te conviennent le mieux",
    noResults: "Aucun résultat — réessaie.",
    recommendedBadge: "Recommandée",
    retake: "Refaire le test",
    questions: [
      {
        q: "Quand tu étudies quelque chose de nouveau, qu'est-ce qui t'aide le plus à comprendre ?",
        options: [
          "L'expliquer à voix haute à quelqu'un (ou à moi-même)",
          "Voir des schémas, des connexions et des dessins",
          "Répéter à voix haute sans regarder le livre",
        ],
      },
      {
        q: "As-tu du mal à commencer à étudier ?",
        options: [
          "Beaucoup, je procrastine toujours",
          "Assez, mais après un moment je me lance",
          "Pas trop, je commence tout de suite",
        ],
      },
      {
        q: "Comment prends-tu tes notes en cours ?",
        options: [
          "J'écris pratiquement tout",
          "Seulement les mots-clés",
          "J'écoute, je réécris à la maison",
        ],
      },
      {
        q: "Au bout de combien de temps as-tu tendance à oublier ce que tu as étudié ?",
        options: [
          "Après quelques jours",
          "Après une semaine ou deux",
          "Je m'en souviens longtemps",
        ],
      },
      {
        q: "Comment prépares-tu généralement un contrôle ?",
        options: [
          "Révision intensive la veille",
          "Un peu de révision chaque jour pendant la semaine",
          "J'étudie tout la veille au soir",
        ],
      },
      {
        q: "Quelle activité t'aide le plus à fixer les informations ?",
        options: [
          "Écrire à la main",
          "Parler, expliquer",
          "Dessiner des cartes, des schémas, des couleurs",
        ],
      },
      {
        q: "Quel type de matières te pèse le plus ?",
        options: [
          "Définitions, vocabulaire, formules",
          "Concepts abstraits à comprendre",
          "Sujets liés entre eux (histoire, littérature)",
        ],
      },
    ],
  },
};
